import React, { useEffect, useRef, useState } from 'react';
import AccountDialog from './AccountDialog';
import { activateCard, activationErrorMessage, looksLikeCardId } from '../../utils/cardActivation';
import styles from './Account.module.css';

export const PHYSICAL_CARD_STORE_URL = 'https://veloxco.com/';

/**
 * One way to add a physical card: link one already in hand, or open the shop.
 * Linking adds a card. It does not replace cards already on the account.
 */
export default function AddPhysicalCardDialog({
  open,
  onClose,
  onLinked,
  initialCardId = '',
  title = 'Add a physical card',
  choiceLead = 'Link one you already have, or order a new one.',
  linkLabel = 'Link a card I already have',
  orderLabel = 'Order a new card',
}) {
  const [step, setStep] = useState('choice');
  const [cardId, setCardId] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setStep(initialCardId ? 'link' : 'choice');
    setCardId(initialCardId || '');
    setError('');
  }, [open, initialCardId]);

  const close = () => {
    if (saving) return;
    onClose?.();
  };

  const link = async () => {
    const value = cardId.trim();
    if (!looksLikeCardId(value)) {
      setError('That does not look like a Velox Card ID. Check the ID and try again.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await activateCard(value);
      onLinked?.(value);
      onClose?.();
    } catch (err) {
      setError(activationErrorMessage(err, 'Could not link that card. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AccountDialog
      open={open}
      onClose={close}
      title={title}
      lead={
        step === 'choice'
          ? choiceLead
          : 'Add this physical card to your account. Your existing cards stay active and all open the same profile.'
      }
      initialFocusRef={step === 'link' ? inputRef : undefined}
      footer={
        step === 'choice' ? (
          <button type="button" className={styles.secondaryButton} onClick={close}>
            Cancel
          </button>
        ) : (
          <>
            <button type="button" className={styles.secondaryButton} onClick={() => { setStep('choice'); setError(''); }} disabled={saving}>
              Back
            </button>
            <button type="button" className={styles.primaryButton} onClick={link} disabled={saving || !cardId.trim()}>
              {saving ? 'Linking…' : 'Link card'}
            </button>
          </>
        )
      }
    >
      {step === 'choice' ? (
        <div className={styles.addChoices}>
          <button type="button" className={styles.addChoice} onClick={() => setStep('link')}>
            <span className={styles.addChoiceTitle}>{linkLabel}</span>
            <span className={styles.addChoiceText}>Use a Velox card already in your possession.</span>
          </button>
          <a className={styles.addChoice} href={PHYSICAL_CARD_STORE_URL} target="_blank" rel="noopener noreferrer">
            <span className={styles.addChoiceTitle}>{orderLabel}</span>
            <span className={styles.addChoiceText}>Get a new Velox physical card.</span>
          </a>
        </div>
      ) : (
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="add-physical-card-id">Card ID</label>
          <input
            ref={inputRef}
            id="add-physical-card-id"
            className={styles.input}
            value={cardId}
            onChange={(event) => {
              setCardId(event.target.value);
              setError('');
            }}
            placeholder="Card ID"
            autoComplete="off"
            spellCheck="false"
          />
          {error ? <p className={styles.formError} role="alert">{error}</p> : null}
        </div>
      )}
    </AccountDialog>
  );
}
